<template>
  <div class="cpadp-root">
    <div v-if="curQuestion" class="cpadp-overlay">
      <div class="cpadp-header">
        <div class="cpadp-title"><i class="el-icon-monitor"></i> {{ isCpaQuestion ? 'CPA DEMO SESSION' : 'SLIDE DEMO SESSION' }}</div>
        <div class="cpadp-close" @click="closeDemo"><i class="el-icon-close"></i></div>
      </div>

      <div v-if="curQuestion" class="cpadp-scroll-container" ref="scrollContainer">
        <div class="cpadp-content-stack">
          <!-- SECTION 1: QUESTION DETAILS (TOP) -->
          <div class="cpadp-card cpadp-question-card">
            <div class="q-card-header">
              <!-- DESKTOP HEADER -->
              <template v-if="!isMobile">
                <div class="q-header-left" v-if="isCpaQuestion">
                  <div class="bloom-info">
                    <div class="bloom-line">
                      <span class="bloom-label">BLOOM LEVEL {{ cpaBloomLevel }}</span>
                      <span class="bloom-verb">{{ cpaBloomVerb }}</span>
                    </div>
                    <div class="points-badge">{{ cpaBloomPoints }} POINTS</div>
                  </div>

                  <div class="level-controls">
                    <button class="level-btn btn-down" @click="cpaLevelDown" :disabled="cpaCurrentLevelIndex <= 0">
                      LEVEL DOWN <i class="el-icon-arrow-down"></i>
                    </button>
                    <button class="level-btn btn-up" @click="cpaLevelUp" :disabled="cpaCurrentLevelIndex >= cpaBloomLevels.length - 1">
                      LEVEL UP <i class="el-icon-arrow-up"></i>
                    </button>
                  </div>
                </div>

                <div class="q-header-left" v-else>
                  <div class="q-title-only">{{ curQuestion.title || 'SLIDE DOCUMENT' }}</div>
                </div>

                <div class="q-header-right" v-if="isCpaQuestion">
                  <button class="support-btn" @click="handleShowCpa"><i class="el-icon-question"></i> SUPPORT QUESTIONS</button>
                  <div class="question-count-badge">Question 1 of 3</div>
                </div>
              </template>

              <!-- MOBILE HEADER (Mirrors GameOnStudentDashboard style) -->
              <template v-else>
                <div class="q-header-mobile full-width" v-if="isCpaQuestion">
                  <div class="bloom-info">
                    <div class="bloom-line">
                      <span class="bloom-label">BLOOM {{ cpaBloomLevel }}</span>
                      <span class="bloom-verb">{{ cpaBloomVerb }}</span>
                    </div>
                    <div class="points-badge">{{ cpaBloomPoints }} PTS</div>
                  </div>

                  <div class="q-header-right-mobile">
                    <div class="question-count-badge">Q. {{ cpaCurrentLevelIndex + 1 }}/3</div>
                    <div class="mobile-more-wrap">
                      <button class="btn-more" @click.stop="showMobileMenu = !showMobileMenu">
                        <i class="el-icon-more" style="transform: rotate(90deg)"></i>
                      </button>
                      <!-- Mobile Level Menu -->
                      <transition name="fade">
                        <div v-if="showMobileMenu" class="mobile-level-menu" v-click-outside="() => (showMobileMenu = false)">
                          <div class="mlm-item" @click="cpaLevelUp" :class="{disabled: cpaCurrentLevelIndex >= cpaBloomLevels.length - 1}">
                            LEVEL UP <i class="el-icon-arrow-up"></i>
                          </div>
                          <div class="mlm-item" @click="cpaLevelDown" :class="{disabled: cpaCurrentLevelIndex <= 0}">
                            LEVEL DOWN <i class="el-icon-arrow-down"></i>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
                <div class="q-title-only" v-else>{{ curQuestion.title || 'SLIDE DOCUMENT' }}</div>
              </template>
            </div>

            <!-- Question Body (CPA or Normal) -->
            <div v-if="isCpaQuestion" class="q-content-box">
              <button class="q-expand-btn" @click="isExpanded = !isExpanded">
                <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
              </button>

              <div v-show="isExpanded">
                <div class="q-text" v-html="cpaQuestionHtml"></div>

                <div class="options-list" style="font-size: 16px">
                  <div v-for="(option, i) in cpaCurrentOptions" :key="option._id || i" class="option-item">
                    <span class="option-letter">{{ 'ABCD'[i] }}.</span>
                    <span class="option-val">{{ option.val || option.data || option.text || option }}</span>
                  </div>
                </div>

                <div v-if="curQuestion.multi" class="multi-notice">
                  <i class="el-icon-info"></i>
                  This question may have <span>multiple correct answers</span>
                </div>
              </div>
              <div v-show="!isExpanded" class="q-text-clamped" v-html="cpaQuestionHtml"></div>
            </div>

            <!-- NORMAL SLIDE VIEW -->
            <div v-else class="normal-slide-view">
              <div class="ns-thumbnail-wrap">
                <img v-if="curQuestion.pic" :src="Fn.hashToUrl(curQuestion.pic)" />
                <div class="ns-view-link" @click="showSlidePopup = true"><i class="el-icon-view"></i> VIEW</div>
              </div>
            </div>
          </div>

          <!-- CPA SPLIT LAYOUT -->
          <template v-if="isCpaQuestion">
            <div class="cpa-split-layout">
              <!-- Left Side: Support Questions -->
              <div class="cpa-split-panel">
                <div class="cpa-split-header">
                  <div class="cpa-split-title">SUPPORT QUESTIONS</div>
                  <div class="cpa-view-toggle">
                    <button :class="{'active': activeCpaView === 'pictorial'}" @click="activeCpaView = 'pictorial'">Pictorial View</button>
                    <button :class="{'active': activeCpaView === 'concrete'}" @click="activeCpaView = 'concrete'">Concrete View</button>
                  </div>
                </div>
                <div class="cpa-split-body">
                  <div class="cpa-split-grid-bg"></div>
                  <img v-if="activeCpaView === 'pictorial' && curQuestion.pictorial?.cover" :src="Fn.hashToUrl(curQuestion.pictorial.cover)" class="cpa-split-image" />
                  <img v-if="activeCpaView === 'concrete' && curQuestion.concrete?.cover" :src="Fn.hashToUrl(curQuestion.concrete.cover)" class="cpa-split-image" />
                  <div v-if="(activeCpaView === 'pictorial' && !curQuestion.pictorial?.cover) || (activeCpaView === 'concrete' && !curQuestion.concrete?.cover)" class="cpa-no-image">
                    No {{activeCpaView}} image available
                  </div>
                </div>
              </div>

              <!-- Right Side: Student's Answer -->
              <div class="cpa-split-panel">
                <div class="cpa-split-header">
                  <div class="cpa-split-title">YOUR WORKSHEET</div>
                </div>
                <div class="cpadp-canvas-container cpa-split-canvas">
                  <iframe v-if="cpaDrawUrl" :src="cpaDrawUrl" frameborder="0" @load="handleIframeLoad" tabindex="-1"></iframe>
                  <div v-else class="canvas-loading">
                    <i class="el-icon-loading"></i>
                    <span>LOADING WORKSHEET...</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- NORMAL LAYOUT -->
          <template v-else>
            <!-- SECTION 2: WORKSHEET HEADER (MIDDLE) -->
            <div class="cpadp-worksheet-header">
              <div class="ws-title">
                <div class="ws-main"><i class="el-icon-edit"></i> Your Worksheet</div>
                <div class="ws-sub">SOLVE THE PROBLEM HERE</div>
              </div>
              <div class="ws-actions">
                <!-- Action buttons removed per user request -->
              </div>
            </div>

            <!-- SECTION 3: DRAWING CANVAS (BOTTOM) -->
            <div class="cpadp-canvas-container">
              <iframe v-if="cpaDrawUrl" :src="cpaDrawUrl" frameborder="0" @load="handleIframeLoad" tabindex="-1"></iframe>
              <div v-else class="canvas-loading">
                <i class="el-icon-loading"></i>
                <span>LOADING WORKSHEET...</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- IMAGE PREVIEW OVERLAY (Matches GameSettingPannel style) -->
      <div v-if="showSlidePopup" class="gs-preview-overlay" @click.stop="showSlidePopup = false">
        <div class="gs-preview-close" @click.stop="showSlidePopup = false"><i class="el-icon-close"></i></div>
        <img v-if="curQuestion && curQuestion.pic" :src="Fn.hashToUrl(curQuestion.pic)" alt="Preview" class="gs-preview-image" @click.stop />
        <div v-else class="gs-preview-error">No image available</div>
      </div>

      <!-- CPA SUPPORT OVERLAY -->
      <transition name="fade">
        <div v-if="showCpaBox" class="cpa-support-overlay" @click.self="showCpaBox = false">
          <div class="cpa-modal-container">
            <CpaBox :question="curQuestion" @close="showCpaBox = false" />
          </div>
        </div>
      </transition>
      <!-- FLOATING SUPPORT BUTTON (Mobile) -->
      <button v-if="isMobile && isCpaQuestion" class="fab-support" @click="handleShowCpa">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="display: block">
          <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z"></path>
          <path d="M9 18h6"></path>
          <path d="M10 22h4"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import CpaBox from './CpaBox.vue'

export default {
  name: 'DemoReplayPage',
  components: {
    CpaBox,
  },
  props: {
    curQuestion: {
      type: Object,
      required: true,
    },
    roomId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      cpaBloomLevels: ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'],
      cpaCurrentLevelIndex: 0,
      cpaDrawUrl: '',
      showSlidePopup: false,
      showCpaBox: false,
      isExpanded: true,
      activeCpaView: 'pictorial',
      isMobile: false,
      showMobileMenu: false,
    }
  },
  watch: {
    curQuestion: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            if (this.$el) {
              this.initDrawUrl()
              this.handleIframeLoad()
            }
          })
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    Fn() {
      return window.Fn || {}
    },
    isCpaQuestion() {
      return this.curQuestion && this.curQuestion.cpa && this.curQuestion.cpa.outcome
    },
    cpaBloomLevel() {
      // Assuming bloom data exists on the question
      if (this.curQuestion && this.curQuestion.bloom && this.curQuestion.bloom.length > 0) {
        return this.curQuestion.bloom[0]
      }
      return this.cpaCurrentLevelIndex || 1
    },
    cpaBloomVerb() {
      if (this.curQuestion && this.curQuestion.bloom && this.curQuestion.bloom.length > 0) {
        return this.cpaBloomLevels[this.curQuestion.bloom[0] - 1] || 'Analyze'
      }
      return this.cpaBloomLevels[this.cpaCurrentLevelIndex] || 'Analyze'
    },
    cpaBloomPoints() {
      return this.curQuestion?.points || 2
    },
    cpaQuestionHtml() {
      if (!this.curQuestion) return ''
      return this.curQuestion.abstractData || this.curQuestion.data || (this.curQuestion.abstract && this.curQuestion.abstract.data) || 'Question Data'
    },
    cpaCurrentOptions() {
      if (!this.curQuestion) return []
      return this.curQuestion.options || []
    },
  },
  mounted() {
    document.body.appendChild(this.$el)
    this.initDrawUrl()
    this.handleIframeLoad()
    this.checkMobile()
    if (this.isMobile) {
      this.isExpanded = false
    }
    window.addEventListener('resize', this.checkMobile)
  },
  destroyed() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  directives: {
    'click-outside': {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event)
          }
        }
        document.body.addEventListener('click', el.clickOutsideEvent)
      },
      unbind: function (el) {
        document.body.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    async initDrawUrl() {
      // Generate the draw URL for the iframe synced with projector
      // This follows the collaborative room pattern: /draw/#room={sid},{drawkey}
      if (this.curQuestion && this.roomId) {
        const session = this.$store.state.session
        const drawkey = session.drawkey || (await this.Fn.drawKey())
        if (session.drawkey !== drawkey) {
          await this.$store.dispatch('upSession', {drawkey})
        }
        this.cpaDrawUrl = `/draw/#room=${this.roomId},${drawkey}`
      } else {
        setTimeout(() => {
          // Fallback for demo display purposes
          if (!this.cpaDrawUrl) {
            this.cpaDrawUrl = `/draw/`
          }
        }, 500)
      }
    },
    handleIframeLoad() {
      // Prevent automatic scroll to iframe on load by resetting container scroll to top
      const container = this.$refs.scrollContainer
      if (!container) return

      const doReset = () => {
        if (container) {
          container.scrollTop = 0
        }
      }

      // Execute in a sequence to counteract the focus-driven jump that happens during/after load
      doReset()
      this.$nextTick(doReset)
      setTimeout(doReset, 50)
      setTimeout(doReset, 250)
      setTimeout(doReset, 600)
      setTimeout(doReset, 1200)
    },
    closeDemo() {
      this.$emit('close')
      localStorage.setItem('excalidraw', JSON.stringify([]))
    },
    cpaLevelDown() {
      if (this.cpaCurrentLevelIndex > 0) {
        this.cpaCurrentLevelIndex--
      }
    },
    cpaLevelUp() {
      if (this.cpaCurrentLevelIndex < this.cpaBloomLevels.length - 1) {
        this.cpaCurrentLevelIndex++
      }
    },
    handleShowCpa() {
      if (this.curQuestion && this.curQuestion.cpa) {
        this.showCpaBox = true
      } else {
        this.$message({
          message: 'No CPA support layers available for this question',
          type: 'warning',
        })
      }
    },
  },
}
</script>

<style scoped>
/* MAIN OVERLAY */
.cpadp-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #051416;
  z-index: 200000 !important;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #fff;
}

/* TOP HEADER */
.cpadp-header {
  height: 56px;
  background: #091315;
  border-bottom: 1px solid #1a3c40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
}
.cpadp-title {
  color: #00ffff;
  font-weight: bold;
  font-size: 15px;
  letter-spacing: 1px;
}
.cpadp-close {
  color: #8c99a0;
  font-size: 24px;
  cursor: pointer;
  transition: 0.2s;
}
.cpadp-close:hover {
  color: #fff;
}

/* CONTENT STACK */
.cpadp-scroll-container {
  flex: 1;
  overflow-y: auto;
  padding: 2.5vh 5vw;
}
.cpadp-content-stack {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* CARDS */
.cpadp-card {
  background: #091315;
  border: 1px solid #1a3c40;
  border-radius: 12px;
  padding: 24px;
}

/* QUESTION CARD HEADER */
.q-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.q-header-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;
}
.bloom-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bloom-line {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bloom-label {
  color: #8c99a0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
}
.bloom-verb {
  color: #fff;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
}
.points-badge {
  color: #00ffff;
  font-size: 11px;
  font-weight: 800;
}
.level-controls {
  display: flex;
  gap: 12px;
}
.level-btn {
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid #00ffff;
  border-radius: 6px;
  color: #00ffff;
  font-size: 11px;
  font-weight: 800;
  padding: 8px 16px;
  cursor: pointer;
  transition: 0.2s;
  letter-spacing: 0.5px;
}
.level-btn.btn-up {
  background: #00ffff;
  color: #000;
}
.level-btn:hover:not(:disabled) {
  opacity: 0.8;
}
.level-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  border-color: #1a3c40;
  color: #8c99a0;
}

.q-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.support-btn {
  background: transparent;
  border: 1px solid #ff00ff;
  border-radius: 6px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  letter-spacing: 1px;
  transition: 0.2s;
}
.support-btn:hover {
  background: rgba(255, 0, 255, 0.1);
}

.question-count-badge {
  background: #1c2135;
  color: #8c99a0;
  font-size: 11px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

/* QUESTION CONTENT BOX */
.q-content-box {
  position: relative;
  background: rgba(9, 26, 30, 0.35);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(0, 255, 255, 0.12);
  border-left: 5px solid #00ffff;
  border-radius: 12px;
  padding: 20px 24px;
  transition: all 0.3s ease;
}
.q-expand-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}
.q-expand-btn:hover {
  background: rgba(0, 255, 255, 0.25);
}

/* QUESTION CONTENT */
.q-text {
  font-size: 16px;
  line-height: 1.7;
  color: #e8f4f6;
  margin-bottom: 24px;
  padding-right: 32px;
}
.q-text-clamped {
  font-size: 16px;
  color: #e8f4f6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 40px;
}
.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}
.option-item {
  font-size: 16px;
  display: flex;
  gap: 8px;
  color: #ccd6d9;
}
.option-letter {
  color: #8c99a0;
  font-weight: bold;
}

.multi-notice {
  background: rgba(0, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px 16px;
  color: #8c99a0;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.multi-notice span {
  color: #00ffff;
  font-weight: bold;
}

/* NORMAL SLIDE VIEW */
.ns-thumbnail-wrap {
  width: 240px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  border: 1px solid #1a3c40;
  display: flex;
  flex-direction: column;
}
.ns-thumbnail-wrap img {
  width: 100%;
  display: block;
  filter: brightness(0.85);
}
.ns-view-link {
  padding: 8px;
  color: #00ffff;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #0d2729;
  border-top: 1px solid #1a3c40;
}
.ns-view-link:hover {
  text-decoration: underline;
  background: #113133;
}

.q-title-only {
  font-size: 16px;
  font-weight: bold;
  color: #00ffff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* SUPPORT BAR */
.support-bar {
  border-top: 1px solid #1a3c40;
  margin-top: 32px;
  padding-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.support-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.purple-bold {
  color: #ff00ff;
  font-weight: bold;
  font-size: 14px;
}
.gray-small {
  color: #8c99a0;
  font-size: 11px;
}

.support-actions {
  display: flex;
  gap: 12px;
}
.btn-support {
  background: transparent;
  border: 1px solid #ff00ff;
  border-radius: 8px;
  color: #ff00ff;
  font-size: 12px;
  font-weight: bold;
  padding: 8px 16px;
  cursor: pointer;
}
.btn-ask-ai {
  background: linear-gradient(135deg, #15c39a, #00ffff);
  border: none;
  border-radius: 8px;
  color: #001a1a;
  font-size: 12px;
  font-weight: bold;
  padding: 8px 16px;
  cursor: pointer;
}

/* WORKSHEET HEADER */
.cpadp-worksheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}
.ws-main {
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-main i {
  color: #00ffff;
}
.ws-sub {
  font-size: 10px;
  color: #4a6870;
  font-weight: bold;
  letter-spacing: 1px;
  margin-top: 4px;
}

.ws-actions {
  display: flex;
  gap: 12px;
}
.btn-ws-outline {
  background: transparent;
  border: 1px solid #00ffff;
  border-radius: 6px;
  color: #00ffff;
  font-size: 12px;
  font-weight: bold;
  padding: 10px 18px;
  cursor: pointer;
}
.btn-ws-dark {
  background: #0d2729;
  border: 1px solid #1a3c40;
  border-radius: 6px;
  color: #00ffff;
  font-size: 12px;
  font-weight: bold;
  padding: 10px 18px;
  cursor: pointer;
}
.btn-ws-neon {
  background: #00ffff;
  border: none;
  border-radius: 6px;
  color: #001a1a;
  font-size: 12px;
  font-weight: bold;
  padding: 10px 18px;
  cursor: pointer;
}

/* CANVAS */
.cpadp-canvas-container {
  height: 85vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1a3c40;
  position: relative;
}
.cpadp-canvas-container iframe {
  width: 100%;
  height: 100%;
  border: none;
}
.canvas-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #091315;
  color: #8c99a0;
}

/* CPA SPLIT LAYOUT */
.cpa-split-layout {
  display: flex;
  gap: 24px;
  width: 100%;
}
.cpa-split-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #091315;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1a3c40;
}
.cpa-split-header {
  background: #0d2729;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1a3c40;
}
.cpa-split-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 1px;
  color: #00ffff;
  text-transform: uppercase;
}
.cpa-view-toggle {
  display: flex;
  background: rgba(9, 26, 30, 0.35);
  border: 1px solid #1a3c40;
  border-radius: 6px;
  overflow: hidden;
}
.cpa-view-toggle button {
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  background: transparent;
  color: #8c99a0;
  cursor: pointer;
  transition: 0.2s;
}
.cpa-view-toggle button.active {
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
}
.cpa-split-body {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  overflow: hidden;
}
.cpa-split-grid-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: 40px 40px;
  background-image: linear-gradient(to right, rgba(26, 60, 64, 0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(26, 60, 64, 0.5) 1px, transparent 1px);
  opacity: 0.5;
  z-index: 1;
}
.cpa-split-image {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  z-index: 2;
  position: relative;
}
.cpa-no-image {
  color: #8c99a0;
  font-size: 14px;
  z-index: 2;
  position: relative;
}
.cpa-split-canvas.cpadp-canvas-container {
  flex: 1;
  border: none;
  border-radius: 0;
  height: auto;
  min-height: 600px;
  background: #fff;
}

/* PREVIEW OVERLAY (Custom Popup) */
.gs-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000001; /* Higher than cpadp-overlay */
}

.gs-preview-image {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.8);
}

.gs-preview-close {
  position: absolute;
  top: 30px;
  right: 30px;
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}
.gs-preview-close:hover {
  background: #ff0000;
  transform: rotate(90deg);
}

.gs-preview-error {
  color: #8c99a0;
  font-size: 18px;
  font-weight: bold;
}

/* CPA SUPPORT OVERLAY */
.cpa-support-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 200005;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
}

.cpa-modal-container {
  width: 95%;
  max-width: 700px;
  height: auto;
  max-height: 85vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: modal-fade-in 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* MOBILE OPTIMIZATION */
@media (max-width: 768px) {
  .cpadp-scroll-container {
    padding: 16px 16px 80px 16px;
  }
  .cpadp-content-stack {
    gap: 16px;
  }
  .q-card-header {
    margin-bottom: 16px;
  }
  .bloom-line {
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .bloom-label {
    font-size: 8px;
    flex-shrink: 0;
  }
  .bloom-verb {
    font-size: 12px;
  }
  .points-badge {
    font-size: 8px;
    white-space: nowrap;
  }

  .q-header-mobile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    gap: 8px;
  }
  .q-header-right-mobile {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -2px;
    flex-shrink: 0;
  }
  .question-count-badge {
    padding: 4px 8px;
    font-size: 10px;
    color: #fff;
    background: rgba(28, 33, 53, 0.8);
    white-space: nowrap;
  }

  .btn-more {
    background: transparent;
    border: none;
    color: #fff;
    width: 24px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 20px;
    padding-right: 0;
  }

  .mobile-more-wrap {
    position: relative;
  }
  .mobile-level-menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 160px;
    background: #1c2636;
    border: 1px solid #00ffff;
    border-radius: 8px;
    z-index: 1000;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
  .mlm-item {
    padding: 14px 16px;
    font-size: 12px;
    font-weight: bold;
    color: #00ffff;
    text-transform: uppercase;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(0, 255, 255, 0.1);
  }
  .mlm-item:last-child {
    border-bottom: none;
  }
  .mlm-item.disabled {
    color: #8c99a0;
    opacity: 0.5;
  }

  .fab-support {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 56px;
    height: 56px;
    background: #ff00ff;
    color: #fff;
    border: none;
    border-radius: 50%;
    font-size: 24px;
    box-shadow: 0 0 20px rgba(255, 0, 255, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 200002;
    transition: 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .fab-support:active {
    transform: scale(0.9) rotate(-10deg);
  }

  .q-text,
  .q-text-clamped {
    font-size: 14px;
  }
  .option-item {
    font-size: 14px;
  }

  .multi-notice {
    font-size: 11px;
    padding: 8px 12px;
    gap: 6px;
  }
  .multi-notice span {
    font-size: 11px;
  }

  .cpadp-canvas-container {
    height: 70vh;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
